import { Button } from '@/components/Button'
import {
  ModalDialog,
  ModalDialogActions,
  ModalDialogDescription,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogTitle,
} from '@/components/ModalDialog'
import { useIsSkeleton } from '@/components/Skeleton'
import type { ReactNode } from 'react'

export interface UnlinkDocumentDialogProps {
  /**
   * The title of the dialog
   */
  title: ReactNode

  /**
   * Description of what unlinking will do.
   */
  description: ReactNode

  /**
   * Whether the dialog is open.
   */
  isOpen: boolean

  /**
   * Whether the unlink action is in progress.
   */
  isUnlinkPending: boolean

  /**
   * Whether the delete action is in progress.
   */
  isDeletePending: boolean

  /**
   * Callback when dialog is closed.
   */
  onClose?: () => unknown

  /**
   * Callback when unlink is requested.
   */
  onUnlink?: () => unknown

  /**
   * Callback when delete is requested.
   */
  onDelete?: () => unknown

  /**
   * Whether to show skeleton state.
   */
  isSkeleton?: boolean

  /**
   * Test ID attribute.
   */
  testId?: string

  /**
   * Test ID attribute.
   */
  removeButtonTestId?: string

  /**
   * Test ID attribute.
   */
  deleteButtonTestId?: string

  /**
   * Test ID attribute.
   */
  cancelButtonTestId?: string
}

/**
 * Dialog for confirming unlinking a document from an access.
 */
export function UnlinkDocumentDialog({
  title,
  description,
  isOpen,
  isUnlinkPending,
  isDeletePending,
  onClose,
  onUnlink,
  onDelete,
  isSkeleton,
  testId,
  removeButtonTestId,
  deleteButtonTestId,
  cancelButtonTestId,
}: UnlinkDocumentDialogProps) {
  isSkeleton = useIsSkeleton(isSkeleton)

  const isPending = isUnlinkPending || isDeletePending
  return (
    <ModalDialog
      isOpen={isOpen}
      onClose={onClose}
      isDismissable={!isPending}
      testId={testId}
    >
      <ModalDialogHeader>
        <ModalDialogTitle isSkeleton={isSkeleton}>{title}</ModalDialogTitle>
        <ModalDialogDescription isSkeleton={isSkeleton}>
          {description}
        </ModalDialogDescription>
      </ModalDialogHeader>
      <ModalDialogFooter>
        <ModalDialogActions>
          <Button
            label="Remove"
            onPress={onUnlink}
            isDisabled={isDeletePending || !onDelete}
            isPending={isUnlinkPending}
            isSkeleton={isSkeleton}
            testId={removeButtonTestId}
          />

          <Button
            label="Delete permanently"
            onPress={onDelete}
            isDisabled={isUnlinkPending}
            isPending={isDeletePending}
            isSkeleton={isSkeleton}
            variant="outline"
            testId={deleteButtonTestId}
          />

          <Button
            label="Cancel"
            onPress={onClose}
            variant="text"
            isDisabled={isPending}
            isSkeleton={isSkeleton}
            testId={cancelButtonTestId}
          />
        </ModalDialogActions>
      </ModalDialogFooter>
    </ModalDialog>
  )
}
